import React, { useEffect, useState } from 'react';
import { useAuthContext } from '../../context/auth-context';

//Components
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TableCell from '@mui/material/TableCell';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import { ExpandableRow } from './expandable-row';
import { ResetPasswordDialog } from './reset-password-dialog';
import { RoleDialog } from './role-dialog';
import { PermissionsDialog } from './permissions-dialog';
import { CampsDialog } from './camps-dialog';
import { UserActions } from './user-actions';
import { Modal } from '../shared/modal';
import { GenerateAuthOptions } from './generate-auth-options';
import { CollapsibleTable } from '../shared';
import { ConfirmationDialog } from '../shared';
import { useIsMobile } from '../../hooks/use-is-mobile';
import { isAdministrativeRole, roleLabel } from '../../constants/roles';

//Hook & Constants & actions
import { actionValidator } from './event-handler';
import { useGetAllUsersWithRubricsQuery, useRemoveJudgeFromRubricMutation, useRemoveCampFromUserMutation } from '../../redux/services';

const DEFAULT_ROWS_PER_PAGE = 10;
const PAGE_SIZE_STORAGE_KEY = 'camp-manager:user-table:rows-per-page';
const PAGE_SIZE_OPTIONS = [5, 10, 25, 100];
const SEARCH_DEBOUNCE_MS = 400;

const getInitialRowsPerPage = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_ROWS_PER_PAGE;
  }

  const storedValue = Number.parseInt(window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY), 10);

  return PAGE_SIZE_OPTIONS.includes(storedValue) ? storedValue : DEFAULT_ROWS_PER_PAGE;
};

export const UserTable = React.memo(function () {
  const { activeCamp } = useAuthContext();
  const campId = activeCamp?._id ?? null;
  const isMobile = useIsMobile();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(getInitialRowsPerPage);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [modalProps, setModalProps] = useState({
    showModal: false,
    componentChildren: null,
  });
  const [showDialog, setShowDialog] = useState({
    rubricId: '',
    userId: '',
    open: false,
  });
  const [resetDialog, setResetDialog] = useState({ open: false, user: null });
  const [unassignDialog, setUnassignDialog] = useState({ open: false, user: null });
  const [roleDialog, setRoleDialog] = useState({ open: false, user: null });
  const [permissionsDialog, setPermissionsDialog] = useState({ open: false, user: null });
  const [campsDialog, setCampsDialog] = useState({ open: false, user: null });

  // Reset page when camp changes
  useEffect(() => {
    setPage(0);
    setSearchInput('');
    setSearch('');
  }, [campId]);

  const { data, isLoading, isFetching, refetch } = useGetAllUsersWithRubricsQuery(
    { page: page + 1, limit: rowsPerPage, search, campId },
    { refetchOnMountOrArgChange: true },
  );
  const [removeJudgeFromRubric] = useRemoveJudgeFromRubricMutation();
  const [removeCampFromUser] = useRemoveCampFromUserMutation();

  useEffect(() => {
    const totalPages = data?.pagination?.totalPages || 0;

    if (totalPages === 0) {
      if (page !== 0) {
        setPage(0);
      }
      return;
    }

    const maxPage = Math.max(0, totalPages - 1);

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [data, page]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(rowsPerPage));
    return undefined;
  }, [rowsPerPage]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchInput]);

  const handleOpen = component => {
    setModalProps(curr => {
      return { ...curr, showModal: true, componentChildren: component };
    });
  };

  const handleClose = () => {
    setModalProps(curr => {
      return { ...curr, showModal: false, componentChildren: null };
    });
  };

  const handleClick = event => {
    const res = actionValidator(event.target.id, null, handleClose);
    handleOpen(res);
  };

  const handleOpenConfirmationDialog = (rubricId, userId) => {
    setShowDialog(curr => {
      return { ...curr, open: true, rubricId: rubricId, userId: userId };
    });
  };

  const handleSubmit = async () => {
    await removeJudgeFromRubric({ rubricId: showDialog.rubricId, userId: showDialog.userId });
    handleCloseConfirmationDialog();
  };

  const handleCloseConfirmationDialog = () => {
    setShowDialog(curr => {
      return { ...curr, open: false, rubricId: '', userId: '' };
    });
  };

  const handleOpenResetDialog = user => setResetDialog({ open: true, user });
  const handleCloseResetDialog = () => setResetDialog({ open: false, user: null });

  const handleOpenUnassignDialog = user => setUnassignDialog({ open: true, user });
  const handleCloseUnassignDialog = () => setUnassignDialog({ open: false, user: null });

  const handleOpenRoleDialog = user => setRoleDialog({ open: true, user });
  const handleCloseRoleDialog = () => setRoleDialog({ open: false, user: null });

  const handleOpenPermissionsDialog = user => setPermissionsDialog({ open: true, user });
  const handleClosePermissionsDialog = () => setPermissionsDialog({ open: false, user: null });

  const handleOpenCampsDialog = user => setCampsDialog({ open: true, user });
  const handleCloseCampsDialog = () => setCampsDialog({ open: false, user: null });
  const handleUnassignSubmit = async () => {
    await removeCampFromUser({ userId: unassignDialog.user._id, campId });
    handleCloseUnassignDialog();
    refetch();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = event => {
    setSearchInput(event.target.value);
    setPage(0);
  };

  if (isLoading) {
    return <>Loading...</>;
  }

  const users = data?.results || [];
  const totalUsers = data?.pagination?.total || 0;

  const header = (
    <>
      <TableCell>Nombre</TableCell>
      <TableCell>UserName</TableCell>
      <TableCell>Email</TableCell>
      <TableCell>Role</TableCell>
      <TableCell />
    </>
  );
  const body = (
    <>
      {users.length > 0 ? (
        users.map(user => (
          <ExpandableRow
            key={user._id}
            row={user}
            activeCampId={campId}
            handleShowConfirmationDialog={handleOpenConfirmationDialog}
            handleResetPassword={handleOpenResetDialog}
            handleUnassignCamp={handleOpenUnassignDialog}
            handleChangeRole={handleOpenRoleDialog}
            handleEditPermissions={handleOpenPermissionsDialog}
            handleManageCamps={handleOpenCampsDialog}
          />
        ))
      ) : (
        <TableRow>
          <TableCell align='center' colSpan={4}>
            <Typography color='text.secondary'>No users found for the current filter.</Typography>
          </TableCell>
        </TableRow>
      )}
    </>
  );

  const footer = (
    <TablePagination
      component='div'
      count={totalUsers}
      page={page}
      onPageChange={handleChangePage}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      rowsPerPageOptions={PAGE_SIZE_OPTIONS}
    />
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <GenerateAuthOptions handleClick={handleClick} />
        <TextField
          label='Search users'
          value={searchInput}
          onChange={handleSearchChange}
          size='small'
          sx={{ minWidth: { xs: '100%', sm: 320 } }}
          helperText='Filter by name, username, or role'
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' />
              </InputAdornment>
            ),
            endAdornment: isFetching ? (
              <InputAdornment position='end'>
                <CircularProgress size={18} />
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {/* Mobile: card list */}
      {isMobile && (
        <Stack spacing={1.5}>
          {users.length === 0 ? (
            <Typography color='text.secondary' variant='body2' sx={{ textAlign: 'center', py: 3 }}>
              No users found for the current filter.
            </Typography>
          ) : (
            users.map(user => (
              <Card key={user._id} variant='outlined'>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                    <Typography variant='subtitle2' fontWeight={700}>{user.fullname}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={roleLabel(user.role)}
                        size='small'
                        variant='outlined'
                        color={isAdministrativeRole(user.role) ? 'primary' : 'default'}
                      />
                      <UserActions
                        user={user}
                        activeCampId={campId}
                        onChangeRole={handleOpenRoleDialog}
                        onEditPermissions={handleOpenPermissionsDialog}
                        onManageCamps={handleOpenCampsDialog}
                        onResetPassword={handleOpenResetDialog}
                        onUnassignCamp={handleOpenUnassignDialog}
                      />
                    </Box>
                  </Box>
                  <Typography variant='caption' color='text.secondary' display='block'>
                    @{user.username}
                    {user.zone ? ` · ${user.zone}` : ''}
                  </Typography>
                  {user.email && (
                    <Typography variant='caption' color='text.secondary' display='block'>
                      {user.email}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))
          )}
          <TablePagination
            component='div'
            count={totalUsers}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          />
        </Stack>
      )}

      {/* Desktop: CollapsibleTable */}
      {!isMobile && <CollapsibleTable header={header} body={body} footer={footer} />}

      <ConfirmationDialog
        open={showDialog.open}
        handleClose={handleCloseConfirmationDialog}
        handleSubmit={handleSubmit}
        title='Eliminar asignacion de juez'
        bodyText='Esta seguro que desea eliminar este elemento?'
      />
      <Modal
        componentChildren={modalProps.componentChildren}
        showModal={modalProps.showModal}
        handleClose={handleClose}
      />
      <ResetPasswordDialog
        open={resetDialog.open}
        user={resetDialog.user}
        onClose={handleCloseResetDialog}
      />
      <RoleDialog
        open={roleDialog.open}
        user={roleDialog.user}
        onClose={handleCloseRoleDialog}
      />
      <PermissionsDialog
        open={permissionsDialog.open}
        user={permissionsDialog.user}
        onClose={handleClosePermissionsDialog}
      />
      <CampsDialog
        open={campsDialog.open}
        user={campsDialog.user}
        onClose={handleCloseCampsDialog}
      />
      <ConfirmationDialog
        open={unassignDialog.open}
        handleClose={handleCloseUnassignDialog}
        handleSubmit={handleUnassignSubmit}
        title='Desasignar usuario del campamento'
        bodyText={`¿Estás seguro que deseas desasignar a "${unassignDialog.user?.fullname}" de este campamento?`}
      />
    </>
  );
});
